/**
 * Status cell for a service-provider connection.
 *
 * Whether a connection draws its credential from a separate OAuth connection is a
 * property of its connector, not of the value it happens to store, so this resolves
 * the connector manifest and reads the parameter the selected set declares. A
 * connection carrying its own credential has no live status and reports Connected;
 * an OAuth-backed one reports its backing connection's status. Unknown is reserved
 * for the cases where nothing can be concluded yet — the connector did not resolve,
 * no sign-in has been recorded, or the data-plane list has not loaded.
 */
import { useMemo } from 'react';
import { Badge, Skeleton, SkeletonItem, makeStyles } from '@coreai/fluentui-react';
import { useConnectorMetadata } from '@/hooks/useConnectionMutations';
import { useServiceProviderConnector } from '@/hooks/useServiceProviderConnector';
import { useEffectiveManifestWithFallback } from '@/hooks/useEffectiveManifest';
import {
  buildConnectorManifest,
  getConnectionParameterSets,
} from '@/services/manifestConnectionService';
import {
  getOAuthConnectionId,
  getOAuthConnectionParameterKey,
  isOAuthConnectionParameterSet,
  selectConnectionParameterSet,
} from '@/utils/connections';
import { ConnectionStatus } from '@/types';
import type { AvailableConnection } from '@/types';
import type { ServiceProviderConnection } from '@/types/connections';

const useStyles = makeStyles({
  loadingBadge: {
    width: '72px',
  },
});

export interface ServiceProviderConnectionStatusProps {
  connection: ServiceProviderConnection;
  /** Data-plane connections, where an OAuth-backed connection's real status lives. */
  apiConnections: AvailableConnection[] | undefined;
}

export const ServiceProviderConnectionStatus = ({
  connection,
  apiConnections,
}: ServiceProviderConnectionStatusProps) => {
  const styles = useStyles();
  const connectorId = connection.serviceProvider?.id ?? '';

  const { data: fetchedMetadata, isLoading: isLoadingMetadata } =
    useConnectorMetadata(connectorId);
  const { data: serviceProviderConnector, isLoading: isLoadingSpConnector } =
    useServiceProviderConnector(connectorId || null);

  const connectorManifest = useMemo(
    () =>
      buildConnectorManifest({
        connectorId,
        serviceProviderConnector,
        operationManifest: null,
      }),
    [connectorId, serviceProviderConnector],
  );
  const effectiveManifest = useEffectiveManifestWithFallback(
    connectorManifest,
    fetchedMetadata,
  );

  const parameterSets = useMemo(
    () => getConnectionParameterSets(effectiveManifest),
    [effectiveManifest],
  );
  const parameterSet = useMemo(
    () => selectConnectionParameterSet(parameterSets, connection.parameterSetName),
    [parameterSets, connection.parameterSetName],
  );

  const status = useMemo((): 'connected' | 'notConnected' | 'unknown' => {
    // No parameter sets means the connector never resolved, so the connection cannot be
    // classified either way. Nor can a record whose set name the manifest no longer has.
    if (parameterSets.length === 0 || !parameterSet) return 'unknown';
    if (!isOAuthConnectionParameterSet(parameterSet)) return 'connected';

    const oauthConnectionId = getOAuthConnectionId(
      connection,
      getOAuthConnectionParameterKey(parameterSet),
    );
    if (!oauthConnectionId) return 'unknown';

    // Undefined is the list still loading; an empty list is a loaded, genuinely empty one.
    if (apiConnections === undefined) return 'unknown';

    const target = oauthConnectionId.toLowerCase();
    const backing = apiConnections.find(
      (candidate) => candidate.id?.toLowerCase() === target,
    );
    // The recorded sign-in is gone, which the picker already treats as unusable.
    if (!backing) return 'notConnected';

    return backing.status === ConnectionStatus.Connected ? 'connected' : 'notConnected';
  }, [parameterSets, parameterSet, connection, apiConnections]);

  // Reporting Connected before the connector resolves would flash green on a broken sign-in.
  if (isLoadingMetadata || isLoadingSpConnector) {
    return (
      <Skeleton aria-label="Loading connection status">
        <SkeletonItem shape="rectangle" size={16} className={styles.loadingBadge} />
      </Skeleton>
    );
  }

  if (status === 'unknown') {
    return (
      <Badge appearance="filled" color="informative">
        Unknown
      </Badge>
    );
  }

  const isConnected = status === 'connected';

  return (
    <Badge appearance="filled" color={isConnected ? 'success' : 'danger'}>
      {isConnected ? 'Connected' : 'Not connected'}
    </Badge>
  );
};

ServiceProviderConnectionStatus.displayName = 'ServiceProviderConnectionStatus';
